<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useChatStore } from '@/stores/chat'
import { useFormStore } from '@/stores/form'
import { useUserStore } from '@/stores/user'
import { useChat } from '@/composables/useChat'

import ChatContainer from '@/components/layout/ChatContainer.vue'
import MessageList from '@/components/chat/MessageList.vue'
import ThankYou from '@/components/pages/ThankYou.vue'

// Input components
import DebtSlider from '@/components/inputs/DebtSlider.vue'
import ButtonSelector from '@/components/inputs/ButtonSelector.vue'
import NameInput from '@/components/inputs/NameInput.vue'
import EmailInput from '@/components/inputs/EmailInput.vue'
import AddressInput from '@/components/inputs/AddressInput.vue'
import DobInput from '@/components/inputs/DobInput.vue'
import PhoneInput from '@/components/inputs/PhoneInput.vue'

// Compliance
import TcpaDrawer from '@/components/compliance/TcpaDrawer.vue'

// API Services
import { validateEmail } from '@/services/api/email-validation'
import { validatePhone } from '@/services/api/phone-validation'
import { getGeolocation } from '@/services/api/geolocation'
import { pullCreditData, parseArrayDebt } from '@/services/api/array-credit'
import { postLead } from '@/services/api/lead-posting'
import { sendEmailToAC, sendPhoneToAC, sendAddressToAC, sendArrayDataToAC } from '@/services/api/active-campaign'
import { sendBlueshiftEvent, BlueshiftEvents } from '@/services/api/blueshift'
import type { LeadPostResult } from '@/types'

const chatStore = useChatStore()
const formStore = useFormStore()
const userStore = useUserStore()
const { submitAnswer, initialize } = useChat()

const showTcpaDrawer = ref(false)
const isSubmitting = ref(false)
const isInitialized = ref(false)

const currentInputType = computed(() => chatStore.currentStep?.inputType)
const currentStepOptions = computed(() => chatStore.currentStep?.options || [])

onMounted(async () => {
  // Initialize stores
  userStore.init()
  formStore.restoreFromStorage()

  // Get geolocation
  const geo = await getGeolocation()
  userStore.setGeolocation(geo)
  formStore.setField('ipAddress', geo.ip)

  // Start chat
  await initialize()
  isInitialized.value = true
})

// Handle debt slider submit
async function handleDebtSubmit(value: number) {
  await submitAnswer(value)
}

// Handle button selection (employment)
async function handleButtonSubmit(value: string | number | boolean) {
  formStore.setField('income', String(value))
  await submitAnswer(value)
}

// Handle name submit
async function handleNameSubmit() {
  await submitAnswer(formStore.fullName)
}

// Handle email submit with validation
async function handleEmailValidate() {
  formStore.setValidation('email', { checking: true, error: null })

  const result = await validateEmail(formStore.data.email)

  formStore.setValidation('email', {
    checking: false,
    valid: result.isValid,
    sendToAC: result.sendToAC,
    error: result.isValid ? null : 'Please enter a valid email address',
  })

  if (result.isValid) {
    // Send to AC if valid
    if (result.sendToAC) {
      sendEmailToAC({
        email: formStore.data.email,
        ip: formStore.data.ipAddress,
        firstname: formStore.data.firstName,
        lastname: formStore.data.lastName,
        debt: formStore.data.debtAmount || 0,
        income: formStore.data.income || '0',
        faf: '1',
        seed: '0',
      })
      sendBlueshiftEvent(formStore.data.email, BlueshiftEvents.SIGNUP)
    }

    await submitAnswer(formStore.data.email)
  }
}

// Handle address submit
async function handleAddressSubmit() {
  // Send to AC
  if (formStore.validation.email.sendToAC) {
    sendAddressToAC({
      email: formStore.data.email,
      state: formStore.data.state,
      zip: formStore.data.zip,
      faf: '1',
      seed: '0',
    })
  }

  await submitAnswer(formStore.data.address)
}

// Handle DOB submit - triggers credit pull
async function handleDobSubmit() {
  // Submit answer first to advance
  await submitAnswer(formStore.dob)

  // Trigger Array credit pull in background
  pullCreditData({
    firstName: formStore.data.firstName,
    lastName: formStore.data.lastName,
    address: {
      street: formStore.data.address,
      city: formStore.data.city,
      state: formStore.data.state,
      zip: formStore.data.zip,
    },
    dob: formStore.dobISO,
    userConsent: {
      ipAddress: formStore.data.ipAddress,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    },
  }).then(result => {
    if (result) {
      formStore.setField('arrayCredit', result.credit_score)
      formStore.setField('arrayDebt', result.total_unsecured_debt)
      formStore.setField('arrayCollections', result.collections)
      formStore.setField('arrayDelinquencies', result.delinquencies)
      formStore.setField('arrayTaxLiens', result.tax_liens)
      formStore.setField('arrayStudentLoans', result.open_student_loan_bal)
      formStore.setField('verifiedDebt', parseArrayDebt(result.total_unsecured_debt))

      // Send to AC
      if (formStore.validation.email.sendToAC) {
        sendArrayDataToAC({
          email: formStore.data.email,
          arrayDebt: result.total_unsecured_debt,
          creditScore: result.credit_score,
          collections: result.collections,
          delinquencies: result.delinquencies,
          taxLiens: result.tax_liens,
          studentLoans: result.open_student_loan_bal,
          birthYear: formStore.data.dobYear,
          birthMonth: formStore.data.dobMonth,
          birthDay: formStore.data.dobDay,
        })
      }
    }
  })
}

// Handle phone - show TCPA drawer
function handleShowTcpa() {
  showTcpaDrawer.value = true
}

// Handle TCPA confirmation - validate phone and submit
async function handleTcpaConfirm() {
  showTcpaDrawer.value = false
  formStore.setValidation('phone', { checking: true, error: null })

  const result = await validatePhone(formStore.data.phoneUnmasked)

  formStore.setValidation('phone', {
    checking: false,
    valid: result.isValid && result.isUS,
    error: result.isValid && result.isUS ? null : 'Please enter a valid US phone number',
  })

  if (result.isValid && result.isUS) {
    // Send to AC
    if (formStore.validation.email.sendToAC) {
      sendPhoneToAC({
        email: formStore.data.email,
        phone_number: '+1' + formStore.data.phoneUnmasked,
        firstname: formStore.data.firstName,
        lastname: formStore.data.lastName,
        ip: formStore.data.ipAddress,
        faf: '1',
        seed: '0',
      })
      sendBlueshiftEvent(formStore.data.email, BlueshiftEvents.FULL_SUBMIT)
    }

    // Submit the lead
    await submitLeadToNetwork()
  }
}

// Submit to LeadsPedia
async function submitLeadToNetwork() {
  isSubmitting.value = true
  chatStore.setSubmitting(true)

  // Get Jornaya and TrustedForm tokens
  const jornayaToken = (document.getElementById('leadid_token') as HTMLInputElement)?.value || ''
  const trustedFormCertUrl = (document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement)?.value || ''
  const trustedFormToken = (document.getElementsByName('xxTrustedFormToken')[0] as HTMLInputElement)?.value || ''
  const trustedFormUrl = trustedFormCertUrl || trustedFormToken

  formStore.setField('jornayaLeadId', jornayaToken)
  formStore.setField('trustedFormUrl', trustedFormUrl)
  formStore.setField('trustedFormId', trustedFormUrl)

  const result = await postLead(
    {
      first_name: formStore.data.firstName,
      last_name: formStore.data.lastName,
      email_address: formStore.data.email,
      phone_home: formStore.data.phoneUnmasked,
      address: formStore.data.address,
      city: formStore.data.city,
      state: formStore.data.state,
      zip_code: formStore.data.zip,
      dob: formStore.dob,
      debt_amount: formStore.data.debtAmount || 0,
      income: formStore.data.income || '0',
      ip_address: formStore.data.ipAddress,
      jornaya_lead_id: formStore.data.jornayaLeadId,
      trusted_form_id: formStore.data.trustedFormId,
      tcpaLanguage: formStore.data.tcpaLanguage,
      user_agent: navigator.userAgent,
      landing_page: window.location.href,
      // Array data
      array_credit: formStore.data.arrayCredit || undefined,
      array_debt: formStore.data.arrayDebt || undefined,
      array_collections: formStore.data.arrayCollections || undefined,
      array_delinquencies: formStore.data.arrayDelinquencies || undefined,
      array_tax_liens: formStore.data.arrayTaxLiens || undefined,
      array_student_loans: formStore.data.arrayStudentLoans || undefined,
      verified_debt: formStore.data.verifiedDebt || 0,
      // Tracking
      aff_id: userStore.affId || '0',
      lp_s1: userStore.lpS1,
      lp_s2: userStore.lpS2,
      lp_s3: userStore.lpS3,
      lp_s4: userStore.lpS4,
      lp_s5: userStore.lpS5,
    },
    userStore.affId || '0'
  )

  if (result.success) {
    // Trigger buyer sequence
    sendBlueshiftEvent(formStore.data.email, BlueshiftEvents.BUYER_SEQUENCE)
  }

  // Store result for thank you page
  formStore.setField('advertiserId', result.advertiserId || '')
  formStore.setField('revenue', result.revenue || 0)
  formStore.setField('redirectUrl', result.redirectUrl || '')

  // Add user message
  chatStore.addUserMessage(formStore.data.phone, formStore.data.phone, 'phone')

  // Handle redirects
  handleRedirects(result)
}

// Handle post-submission redirects
function handleRedirects(result: LeadPostResult) {
  // Priority 1: Server redirect URL from LeadsPedia
  if (result.redirectUrl && result.redirectUrl.startsWith('https://')) {
    window.location.href = result.redirectUrl.replace(/\\u0026/g, '&')
    return
  }

  const vd = formStore.data.verifiedDebt || 0
  const revenue = result.revenue || 0

  // Priority 2: Show thank you page for specific advertisers with revenue
  const showThankYou =
    result.advertiserId === '2' || // JG Wentworth
    (result.advertiserId === '24' && vd > 1) || // ARC with verified debt
    result.advertiserId === '30' || // ARC #2
    (result.advertiserId && revenue > 1) // Any advertiser with revenue

  if (showThankYou) {
    chatStore.advanceStep()
    isSubmitting.value = false
    chatStore.setSubmitting(false)
    return
  }

  // Priority 3: TurboDebt redirect for unsold leads
  triggerTurboDebtRedirect(vd, revenue)
}

// Redirect to TurboDebt for unsold/filtered leads
function triggerTurboDebtRedirect(verifiedDebt: number, revenue: number) {
  const turboBase = 'https://www.ab92trk.com/2X2ZMNN/45TJ3T/'

  // Build TurboDebt URL with user data
  const params = new URLSearchParams({
    sub1: 'fa1',
    sub2: userStore.affId || '',
    first: formStore.data.firstName || '',
    last: formStore.data.lastName || '',
    phone: formStore.data.phoneUnmasked || '',
    email: formStore.data.email || '',
    state: formStore.data.state || '',
  })

  // Filters: unsold flag (9949), no debt + no revenue, high debt + no revenue
  if (verifiedDebt === 9949 || (verifiedDebt === 0 && revenue < 1) || (verifiedDebt > 10000 && revenue < 1)) {
    window.location.href = `${turboBase}?${params.toString()}`
  } else {
    // Default: FirstAdvantage programs page
    window.location.href = `https://firstadvantage.io/programs/save.html?vd=${verifiedDebt}`
  }
}
</script>

<template>
  <ChatContainer :show-progress="chatStore.currentStepIndex > 0 && !chatStore.isComplete">
    <!-- Thank you page -->
    <template v-if="chatStore.isComplete">
      <ThankYou />
    </template>

    <!-- Chat flow -->
    <template v-else>
      <MessageList>
        <!-- Current input based on step type - only show after initialized -->
        <template v-if="isInitialized && !chatStore.isTyping && !isSubmitting">
          <DebtSlider
            v-if="currentInputType === 'slider'"
            @submit="handleDebtSubmit"
          />

          <ButtonSelector
            v-else-if="currentInputType === 'buttons'"
            :options="currentStepOptions"
            @submit="handleButtonSubmit"
          />

          <NameInput
            v-else-if="currentInputType === 'name'"
            @submit="handleNameSubmit"
          />

          <EmailInput
            v-else-if="currentInputType === 'email'"
            @validate="handleEmailValidate"
          />

          <AddressInput
            v-else-if="currentInputType === 'address'"
            @submit="handleAddressSubmit"
          />

          <DobInput
            v-else-if="currentInputType === 'dob'"
            @submit="handleDobSubmit"
          />

          <PhoneInput
            v-else-if="currentInputType === 'phone'"
            @showTcpa="handleShowTcpa"
          />
        </template>

        <!-- Loading state -->
        <div v-if="isSubmitting" class="flex justify-center py-8">
          <div class="text-center">
            <svg class="animate-spin w-10 h-10 text-primary mx-auto mb-3" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-600 text-sm">Submitting your information...</p>
          </div>
        </div>
      </MessageList>
    </template>

  </ChatContainer>

  <!-- TCPA Drawer (outside ChatContainer for proper z-index stacking) -->
  <TcpaDrawer
    :is-visible="showTcpaDrawer"
    @confirm="handleTcpaConfirm"
    @close="showTcpaDrawer = false"
  />
</template>
